import { Alert, AlertTitle, Flex, List, ListItem, Text } from '@shared/components';

import { type ErrorDetail } from '../../containers';

export type ImportExamEventExamineesErrorCodes =
  | 'EXAMINEES_EXCEED_AVAILABLE_SEATS'
  | 'MISSING_REQUIRED_FIELD'
  | 'EXAM_CODE_NOT_FOUND'
  | 'DUPLICATED_EMAIL'
  | 'DUPLICATED_ID_NUMBER'
  | 'INVALID_EMAIL_PATTERN'
  | 'FILE_EXCEED_MAXIMUM_SIZE'
  | 'EMAIL_ALREADY_EXIST'
  | 'UPDATE_UNABLE_DUE_THE_KYC_STATUS'
  | 'UPDATE_UNABLE_ON_ALLOCATED_EXAM_COURSE'
  | 'INVALID_ID_CARD_NUMBER_PATTERN'
  | 'INVALID_PASSPORT_NUMBER_PATTERN'
  | 'INVALID_PHONE_NUMBER_PATTERN'
  | 'EXISTING_IN_BLOCKLIST'
  | 'INVALID_NAME_PATTERN'
  | 'INVALID_TITLE_PATTERN'
  | 'INVALID_NAME_ID_TYPE_CITIZEN_CARD_MISMATCH'
  | 'INVALID_NAME_ID_TYPE_PASSPORT_MISMATCH'
  | 'INVALID_TITLE_ID_TYPE_CITIZEN_CARD_MISMATCH'
  | 'INVALID_TITLE_ID_TYPE_PASSPORT_MISMATCH'
  | 'INVALID_ID_TYPE_FOR_PAPER_BASED_EXAM';

const propertyDisplayMapping: Record<string, string> = {
  firstName: 'ชื่อ',
  middleName: 'ชื่อกลาง',
  lastName: 'นามสกุล',
  title: 'คำนำหน้าชื่อ',
};

const statusDisplayMapping: Record<string, string> = {
  APPROVED: 'ผ่านการยืนยันตัวตน (e-KYC) แล้ว',
  PENDING_APPROVAL: 'อยู่ระหว่างรอยืนยันตัวตน (e-KYC)',
};

const IMPORT_EXAMINEES_VIA_FILE_ERROR_MESSAGES: Record<ImportExamEventExamineesErrorCodes, string> =
  {
    EXAMINEES_EXCEED_AVAILABLE_SEATS: 'จำนวนรายชื่อที่นำเข้าเกินจำนวนที่นั่งที่กำหนด',
    MISSING_REQUIRED_FIELD:
      'กรอกข้อมูลไม่ครบถ้วน เช่น ชื่อ, เลขที่บัตรประชาชนหรือเลขที่พาสปอร์ต, อีเมล',
    EXAM_CODE_NOT_FOUND: 'รหัสหลักสูตร (Exam Code) ไม่ถูกต้องหรือไม่มีในระบบ',
    DUPLICATED_EMAIL: 'พบอีเมลซ้ำในไฟล์',
    DUPLICATED_ID_NUMBER: 'พบเลขที่บัตรประชาชนหรือเลขที่พาสปอร์ตซ้ำในไฟล์',
    INVALID_EMAIL_PATTERN: 'รูปแบบอีเมลไม่ถูกต้อง เช่น ไม่มี @ หรือ .com',
    FILE_EXCEED_MAXIMUM_SIZE: 'ขนาดไฟล์เกิน 20 MB ไม่สามารถอัปโหลดได้',
    EMAIL_ALREADY_EXIST: 'ไม่สามารถใช้อีเมลนี้ได้ เนื่องจากมีการลงทะเบียนไว้ในระบบแล้ว',
    UPDATE_UNABLE_DUE_THE_KYC_STATUS:
      'ไม่สามารถอัปเดตข้อมูล{{field}}ได้ เนื่องจากผู้เข้าสอบ{{kycStatus}}',
    UPDATE_UNABLE_ON_ALLOCATED_EXAM_COURSE:
      'ไม่สามารถอัปเดตข้อมูลหลักสูตรได้ เนื่องจากมีการจัดสรรชุดข้อสอบไว้แล้ว',
    INVALID_ID_CARD_NUMBER_PATTERN:
      'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง ต้องมีจำนวน 13 หลักและเป็นตัวเลขเท่านั้น',
    INVALID_PASSPORT_NUMBER_PATTERN:
      'รูปแบบเลขพาสปอร์ตไม่ถูกต้อง กรุณาระบุเป็นตัวอักษร A-Z หรือเลข 0-9 และต้องมีความยาว 6-9 ตัวอักษร',
    INVALID_PHONE_NUMBER_PATTERN:
      'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง ต้องมีจำนวน 10 หลักและเป็นตัวเลขเท่านั้น',
    EXISTING_IN_BLOCKLIST: 'ไม่สามารถนำเข้ารายชื่อได้ เนื่องจากผู้เข้าสอบถูกระงับสิทธิ์การสอบ',
    INVALID_NAME_PATTERN:
      "รูปแบบชื่อ-นามสกุลไม่ถูกต้อง ต้องมีตัวอักษรภาษาไทย ภาษาอังกฤษ หรืออักษรพิเศษที่กำหนด (.'-) เท่านั้น",
    INVALID_TITLE_PATTERN: 'คำนำหน้าชื่อไม่ตรงกับรายการที่กำหนด กรุณาตรวจสอบจากไฟล์ตัวอย่าง',
    INVALID_NAME_ID_TYPE_CITIZEN_CARD_MISMATCH:
      'ชื่อ-นามสกุลต้องเป็นภาษาไทย สำหรับผู้ใช้เลขที่บัตรประชาชน',
    INVALID_NAME_ID_TYPE_PASSPORT_MISMATCH:
      'ชื่อ-นามสกุลต้องเป็นภาษาอังกฤษ สำหรับผู้ใช้เลขที่พาสปอร์ต',
    INVALID_TITLE_ID_TYPE_CITIZEN_CARD_MISMATCH:
      'คำนำหน้าชื่อต้องเป็นภาษาไทย สำหรับผู้ใช้เลขที่บัตรประชาชน',
    INVALID_TITLE_ID_TYPE_PASSPORT_MISMATCH:
      'คำนำหน้าชื่อต้องเป็นภาษาอังกฤษ สำหรับผู้ใช้เลขที่พาสปอร์ต',
    INVALID_ID_TYPE_FOR_PAPER_BASED_EXAM: 'รอบสอบนี้ไม่รองรับผู้สมัครชาวต่างชาติ',
  };

interface ImportExamineesViaFileErrorAlertProps {
  errors?: Map<string, ErrorDetail[]> | null;
}

export default function ImportExamineesViaFileErrorAlert({
  errors,
}: Readonly<ImportExamineesViaFileErrorAlertProps>) {
  if (!errors?.size) return null;

  const prioritizedCodes: ImportExamEventExamineesErrorCodes[] = [
    'INVALID_ID_TYPE_FOR_PAPER_BASED_EXAM',
    'MISSING_REQUIRED_FIELD',
    'DUPLICATED_EMAIL',
    'DUPLICATED_ID_NUMBER',
    'FILE_EXCEED_MAXIMUM_SIZE',
    'INVALID_ID_CARD_NUMBER_PATTERN',
    'INVALID_PHONE_NUMBER_PATTERN',
  ];

  let isContainsInvalidErrorCodes = false;

  const groupedMessages = new Map<string, number[]>();

  for (const [code, details] of errors.entries()) {
    for (const detail of details || []) {
      const { property, details: extra } = detail;

      const template =
        IMPORT_EXAMINEES_VIA_FILE_ERROR_MESSAGES[code as ImportExamEventExamineesErrorCodes];

      if (!template) {
        isContainsInvalidErrorCodes = true;
        continue;
      }

      const field = propertyDisplayMapping[property ?? ''] ?? property ?? '';
      const kycStatus = statusDisplayMapping[extra?.kycStatus ?? ''] ?? extra?.kycStatus ?? '';

      const message = template.replace('{{field}}', field).replace('{{kycStatus}}', kycStatus);

      const rows = extra?.targetRows ?? [];

      if (!groupedMessages.has(message)) {
        groupedMessages.set(message, []);
      }

      const existingRows = groupedMessages.get(message);
      if (!existingRows) continue;

      for (const row of rows) {
        if (!existingRows.includes(row)) {
          existingRows.push(row);
        }
      }
    }
  }

  const groupedList: [string, number[]][] = [];
  for (const [message, rows] of groupedMessages.entries()) {
    groupedList.push([message, rows.sort((a, b) => a - b)]);
  }

  const prioritizedListFinal: [string, number[]][] = [];
  const otherListFinal: [string, number[]][] = [];

  for (const [message, rows] of groupedList) {
    const matchedCode = prioritizedCodes.find((code) =>
      IMPORT_EXAMINEES_VIA_FILE_ERROR_MESSAGES[code]?.includes(message.split(' ')[0]),
    );

    if (matchedCode) {
      prioritizedListFinal.push([message, rows]);
    } else {
      otherListFinal.push([message, rows]);
    }
  }

  prioritizedListFinal.sort((a, b) => a[1][0] - b[1][0]);
  otherListFinal.sort((a, b) => a[1][0] - b[1][0]);

  const listToRender = [...prioritizedListFinal, ...otherListFinal];

  return (
    <Alert
      severity='error'
      sx={{
        alignItems: 'flex-start',
        '& .MuiAlert-message': { overflow: 'unset' },
        gap: (theme) => theme.spacing(1),
      }}
    >
      <AlertTitle variant='body1Semibold' sx={{ m: 0 }}>
        {isContainsInvalidErrorCodes
          ? 'นำเข้าไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง'
          : 'ไม่สามารถ Import Examinees ได้'}
      </AlertTitle>

      {isContainsInvalidErrorCodes ? null : (
        <Flex sx={{ flexDirection: 'column' }}>
          <Text variant='body2'>กรุณาตรวจสอบและแก้ไขข้อมูลต่อไปนี้ก่อนนำเข้าใหม่:</Text>
          <List sx={{ listStyle: 'disc', p: (theme) => theme.spacing(0, 0, 0, 6) }}>
            {listToRender.map(([message, rows], index) => (
              <ListItem
                key={`${message}-${index}`}
                sx={(theme) => ({
                  display: 'list-item',
                  p: theme.spacing(0),
                  ...theme.typography.body2,
                })}
              >
                {rows.length ? `แถวที่ ${rows.join(', ')}: ${message}` : `${message}`}
              </ListItem>
            ))}
          </List>
        </Flex>
      )}
    </Alert>
  );
}
